import { Box, Center, HStack, Stack, Text, useBreakpointValue } from '@chakra-ui/react'
import BuyNowButton from 'components/buttons/BuyNowButton'
import ImageBox from 'components/common/ImageBox'
import ExternalLink from 'components/links/ExternalLink'
import { StudyCitationLink } from 'components/modules/patientArea/products/types'
import { BadgeCheck } from 'components/svg/BadgeCheck'
import { Body1, H1 } from 'components/typography'
import { useIsSmartfitClient } from 'hooks/helpers/useIsSmartfitClient'
import { HERO_SECTION_ID } from 'layouts/LadingLayout/components/Navbar/components/NavbarDesktop'
import { NAVBAR_MOBILE_HEIGHT } from 'layouts/LadingLayout/components/Navbar/components/NavbarMobile'
import { isEqual } from 'lodash'
import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useState } from 'react'
import {
  MEN_POWER_CHECKOUT_INITIAL_STEP,
  resetCheckoutMenPowerState,
  setActiveVariantId
} from 'redux/slices/checkout-men-power-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'

import { CustomButtonVariant, infoItemsProps } from './BuyNowSection'

interface ProductBannerSectionProps {
  heading: string
  subheading: string[]
  bulletPoints: { bulletIcon?: ReactNode; text: string }[]
  bgImage: string
  studies?: StudyCitationLink[]
  infoItemsList?: infoItemsProps[]
}

const ProductBannerSection: FC<ProductBannerSectionProps> = ({
  heading,
  subheading,
  bulletPoints,
  bgImage,
  studies,
  infoItemsList
}) => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const [activeItem, setActiveItem] = useState<infoItemsProps | null>(null)
  const { activeVariantId } = useAppSelector((state) => state.checkoutMenPower)
  const isSmartfit = useIsSmartfitClient()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const handleVariantSelect = useCallback(
    (variantId: string | number) => {
      dispatch(setActiveVariantId(variantId))
    },
    [dispatch]
  )

  const handleNavigateCheckout = useCallback(() => {
    dispatch(resetCheckoutMenPowerState())
    dispatch(setActiveVariantId(activeVariantId))
    router.push(MEN_POWER_CHECKOUT_INITIAL_STEP)
  }, [dispatch, router, activeVariantId])

  const handleOnClickButton = (item: infoItemsProps) => {
    setActiveItem(item)
    handleVariantSelect(item?.variantId ?? '')
  }

  return (
    <>
      <Stack
        id={HERO_SECTION_ID}
        minH="100vh"
        w="full"
        spacing="none"
        pos="relative"
        color="alabaster"
        px="6"
        pt={`${NAVBAR_MOBILE_HEIGHT + 24}px`}
        direction={{
          base: 'column',
          lg: 'row'
        }}
      >
        <Box pos="absolute" top="0" left="0" w="full" h="full" bg="darkchoc" zIndex={-1} />

        <Center flex={1}>
          <Stack spacing={8} maxW={524}>
            <H1>{heading}</H1>
            {subheading.map((text, index) => (
              <Body1 key={index}>{text}</Body1>
            ))}
            <Stack spacing={4}>
              {bulletPoints.map(({ text, bulletIcon }, index) => (
                <HStack key={index}>
                  {bulletIcon || <BadgeCheck />}
                  <Body1>{text}</Body1>
                </HStack>
              ))}
            </Stack>
            {!isMobile && (
              <Stack>
                {infoItemsList?.map((item) => {
                  const isActive = isEqual(activeItem, item)
                  return (
                    <CustomButtonVariant
                      key={item.variantId}
                      item={item}
                      isActive={isActive}
                      isSmartfit={isSmartfit}
                      handleOnClickButton={handleOnClickButton}
                    />
                  )
                })}
                <BuyNowButton
                  w="fit-content"
                  py={4}
                  size="lg"
                  fontSize="xl"
                  shadow="none"
                  onClick={handleNavigateCheckout}
                >
                  Comprar agora
                </BuyNowButton>
              </Stack>
            )}
          </Stack>
        </Center>
        <Stack flex={1}>
          <ImageBox
            h="full"
            w="full"
            maxW={720}
            minH={320}
            alt="+lib Banner"
            src={bgImage}
            imgProps={{
              objectFit: 'contain',
              objectPosition: 'bottom center'
            }}
          />
        </Stack>
      </Stack>
      {!!studies?.length && (
        <HStack p={6} justify="center" bg={'grayscale.20'}>
          <Text fontSize="sm">
            *Com base em estudos e artigos científicos como:
            {studies
              .map(({ label, href }) => (
                <ExternalLink key={label} href={href} textDecoration="none" color="phthalo">
                  {label}
                </ExternalLink>
              ))
              .reduce((prev, curr) => [prev, ', ', curr] as any)}
          </Text>
        </HStack>
      )}
    </>
  )
}

export default ProductBannerSection
